import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, createClient, subscribe, type PendingRequest, type RequestOptions, type Schemas } from "@polaris/api";
import { Button, Dialog, useToast } from "@polaris/ui";
import { t } from "./i18n";

export type Me = Schemas["Me"];

let csrfToken: string | undefined;
const PENDING = "polaris:pending";

/** Guarda a ação que pediu reautenticação e vai ao login com step-up (RF-ADM-SEG-03). */
function onAuth(err: ApiError, pending: PendingRequest) {
  if (err.code === "step_up_required" && pending.method !== "GET") {
    try {
      sessionStorage.setItem(PENDING, JSON.stringify({ ...pending, at: Date.now() }));
    } catch {
      /* sem armazenamento: o usuário repete a ação */
    }
  }
  if (err.loginUrl) window.location.assign(err.loginUrl);
}

export const api = createClient({
  csrf: () => csrfToken,
  returnPath: () => window.location.pathname + window.location.search,
  onAuth,
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false },
  },
});

/** Repete a ação pendente depois de voltar do step-up, uma única vez. */
async function replayPending(toast: (m: string, k?: "error" | "good") => void) {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING);
    sessionStorage.removeItem(PENDING);
  } catch {
    return;
  }
  if (!raw) return;
  const p = JSON.parse(raw) as PendingRequest & { at: number };
  if (Date.now() - p.at > 10 * 60_000) return;
  try {
    await api.request(p.method as any, p.path, p.options as RequestOptions);
    toast(t("Identidade confirmada: a ação foi concluída."), "good");
    queryClient.invalidateQueries();
  } catch (e) {
    toast(t("A ação não foi concluída: {msg}", { msg: (e as Error).message }), "error");
  }
}

// ----- sessão -----

interface SessionInfo {
  user: { subject: string; name: string; email: string };
  csrf: string;
  idle_timeout_seconds: number;
  expires_at: string;
  step_up_valid_until: string;
  console_version: string;
  api_version: string;
}

interface Ctx {
  me: Me;
  session: SessionInfo;
  can: (action: string, tenant?: string) => boolean;
  areas: string[];
  touch: () => void;
  logout: () => Promise<void>;
}

const SessionCtx = createContext<Ctx | null>(null);

export function useSession(): Ctx {
  const c = useContext(SessionCtx);
  if (!c) throw new Error("sem sessão");
  return c;
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [warn, setWarn] = useState(false);
  const last = useRef(Date.now());

  const load = useCallback(async () => {
    const r = await fetch("/bff/session?return=" + encodeURIComponent(location.pathname + location.search), { credentials: "same-origin" });
    if (r.status === 401) {
      const body = await r.json().catch(() => ({}));
      window.location.assign(body?.error?.login_url ?? "/bff/login");
      return;
    }
    const s = (await r.json()) as SessionInfo;
    csrfToken = s.csrf;
    setSession(s);
    try {
      setMe(await api.get<Me>("/api/v1/me"));
    } catch (e) {
      setFatal((e as Error).message);
    }
  }, []);

  useEffect(() => {
    load().then(() => replayPending(toast));
  }, [load, toast]);

  // Aviso um minuto antes de expirar por inatividade (RF-ADM-SEG-06).
  useEffect(() => {
    if (!session) return;
    const mark = () => { last.current = Date.now(); };
    ["keydown", "pointerdown"].forEach((e) => window.addEventListener(e, mark));
    const iv = setInterval(() => {
      const idle = (Date.now() - last.current) / 1000;
      if (idle > session.idle_timeout_seconds - 60) setWarn(true);
      if (idle > session.idle_timeout_seconds) window.location.assign("/bff/login?return=" + encodeURIComponent(location.pathname));
    }, 10_000);
    return () => {
      clearInterval(iv);
      ["keydown", "pointerdown"].forEach((e) => window.removeEventListener(e, mark));
    };
  }, [session]);

  const touch = useCallback(() => {
    last.current = Date.now();
    setWarn(false);
    api.get("/api/v1/me").catch(() => {});
  }, []);

  const value = useMemo<Ctx | null>(() => {
    if (!me || !session) return null;
    const perms = (me.permissions ?? {}) as Record<string, string>;
    const own = me.principal?.own ?? [];
    return {
      me, session, areas: me.areas ?? [], touch,
      can: (action, tenant) => {
        const s = perms[action];
        if (!s) return false;
        if (s === "all" || s === "explicit") return true;
        return !tenant || own.includes(tenant);
      },
      logout: async () => {
        const r = await api.post<{ logout_url: string }>("/bff/logout".replace("/api", ""));
        window.location.assign(r?.logout_url ?? "/");
      },
    };
  }, [me, session, touch]);

  if (fatal) {
    return (
      <main className="err-page">
        <h1>{t("Acesso negado")}</h1>
        <p>{fatal}</p>
        <p><a href="/bff/login">{t("Entrar de novo")}</a></p>
      </main>
    );
  }
  if (!value) return <main className="err-page"><p>{t("Carregando")}…</p></main>;
  return (
    <SessionCtx.Provider value={value}>
      {children}
      <Dialog open={warn} title={t("Sua sessão vai expirar por inatividade")} onClose={touch}
        footer={<Button variant="primary" onClick={touch}>{t("Continuar conectado")}</Button>}>
        <p>{t("Por segurança, o console encerra a sessão após {n} minutos sem uso.", { n: Math.round(session!.idle_timeout_seconds / 60) })}</p>
      </Dialog>
    </SessionCtx.Provider>
  );
}

// ----- dados -----

/** Consulta GET na Admin API, com a chave incluindo a query. */
export function useApi<T = any>(path: string | null, query?: RequestOptions["query"], opts?: { refetchInterval?: number }) {
  return useQuery<T, ApiError>({
    queryKey: [path, query],
    queryFn: ({ signal }) => api.get<T>(path!, { query, signal }),
    enabled: !!path,
    refetchInterval: opts?.refetchInterval,
  });
}

/** Mutação com toast de sucesso e invalidação. Erros ficam em .error para o ErrorBox. */
export function useAction<V = any, R = any>(fn: (v: V) => Promise<R>, opts?: { success?: string | ((r: R) => string); invalidate?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<R, ApiError, V>({
    mutationFn: fn,
    onSuccess: (r) => {
      if (opts?.success) toast(typeof opts.success === "function" ? opts.success(r) : opts.success, "good");
      if (opts?.invalidate !== false) qc.invalidateQueries();
    },
  });
}

/** Mantém as telas atualizadas com o SSE (RNF-ADM-02). */
export function useLiveEvents() {
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => qc.invalidateQueries(), 300);
    };
    const stop = subscribe((type, data) => {
      if (type === "alert" && data?.data?.severity === "critical") toast(data.data.title, "error");
      refresh();
    });
    return () => { stop(); clearTimeout(timer); };
  }, [qc, toast]);
}

// ----- filtro global de tenant e filtros da URL -----

/** O seletor global de tenant vive na URL (?tenant=), compartilhável (seção 3). */
export function useTenant(): [string, (t: string) => void] {
  const [params, setParams] = useSearchParams();
  const tenant = params.get("tenant") ?? "";
  const set = (v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set("tenant", v);
    else p.delete("tenant");
    p.delete("offset");
    setParams(p, { replace: true });
  };
  return [tenant, set];
}

/** Filtros de lista na URL: link direto reproduz a tela (seção 3). */
export function useFilters<T extends Record<string, string>>(defaults: T): [T, (patch: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();
  const value = { ...defaults } as T;
  for (const k of Object.keys(defaults)) {
    const v = params.get(k);
    if (v !== null) (value as any)[k] = v;
  }
  const set = (patch: Partial<T>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "" || v === (defaults as any)[k]) p.delete(k);
      else p.set(k, String(v));
    }
    if (!("offset" in patch)) p.delete("offset");
    setParams(p, { replace: true });
  };
  return [value, set];
}

// ----- formatação -----

export const fmt = {
  usd: (v?: number) => "US$ " + (v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  brl: (v?: number) => "R$ " + (v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  num: (v?: number, d = 0) => (v ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: d }),
  pct: (v?: number, d = 1) => ((v ?? 0) * 100).toLocaleString("pt-BR", { maximumFractionDigits: d }) + "%",
  ms: (v?: number) => (v ?? 0) >= 1000 ? ((v ?? 0) / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + " s" : Math.round(v ?? 0) + " ms",
  date: (v?: string) => (v && !v.startsWith("0001") ? new Date(v).toLocaleString("pt-BR") : "—"),
  day: (v?: string) => (v && !v.startsWith("0001") ? new Date(v).toLocaleDateString("pt-BR") : "—"),
  rel: (v?: string) => {
    if (!v || v.startsWith("0001")) return "—";
    const s = (Date.now() - new Date(v).getTime()) / 1000;
    if (s < 60) return t("agora");
    if (s < 3600) return t("há {n} min", { n: Math.round(s / 60) });
    if (s < 86400) return t("há {n} h", { n: Math.round(s / 3600) });
    return t("há {n} dias", { n: Math.round(s / 86400) });
  },
};

export const periods = [
  { id: "1h", label: "Última hora" },
  { id: "24h", label: "Últimas 24 horas" },
  { id: "168h", label: "Últimos 7 dias" },
  { id: "720h", label: "Últimos 30 dias" },
];

/** Converte "24h" no from= da API. */
export function fromPeriod(p: string): string {
  return p;
}

export function pretty(v: unknown): string {
  return JSON.stringify(v ?? null, null, 2);
}

/** Envia erros de front-end ao BFF, que registra na mesma stack de logs (RNF-ADM-10). */
export function installErrorReporting() {
  const send = (message: string, stack?: string) => {
    const body = JSON.stringify({ message, stack, route: location.pathname, at: new Date().toISOString() });
    if (navigator.sendBeacon) navigator.sendBeacon("/bff/client-errors", new Blob([body], { type: "application/json" }));
    else fetch("/bff/client-errors", { method: "POST", body, headers: { "Content-Type": "application/json" } }).catch(() => {});
  };
  window.addEventListener("error", (e) => send(e.message, e.error?.stack));
  window.addEventListener("unhandledrejection", (e) => send(String(e.reason?.message ?? e.reason), e.reason?.stack));
}
